import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Ruler, Layers, Hammer, ArrowLeft } from "lucide-react";

// Standalone construction estimator (quantities only — no cart).
export default function BuildCalculator() {
  const [length, setLength] = useState(20);   // wall length, feet
  const [height, setHeight] = useState(8);     // wall height, feet
  const [openings, setOpenings] = useState(0); // total openings area, sq ft
  const [blockType, setBlockType] = useState("6");
  const [wallCount, setWallCount] = useState(1);

  const r = useMemo(() => {
    const L = Math.max(0, Number(length) || 0);
    const H = Math.max(0, Number(height) || 0);
    const walls = Math.max(1, Number(wallCount) || 1);
    const gross = L * H * walls;
    const area = Math.max(0, gross - (Number(openings) || 0));
    // Standard 16"x8" block face ≈ 0.888 sq ft → ~1.125 blocks per sq ft incl. mortar joints
    const blocks = Math.ceil(area * 1.125);
    // Mortar: ~1 bag of cement per 25 blocks (8") / 28 (6")
    const perBag = blockType === "8" ? 25 : 28;
    const cementBags = Math.ceil(blocks / perBag);
    // Sand for mortar: ~3 cu ft sand per bag of cement → cubic yards
    const sandCuYd = +((cementBags * 3) / 27).toFixed(2);
    // Steel: vertical bars every 4 ft + one each end; horizontal every 2 ft of height
    const verticalBars = Math.ceil(L / 4) + 1;
    const horizontalRuns = Math.ceil(H / 2);
    const rebarFt = Math.ceil((verticalBars * H + horizontalRuns * L) * walls);
    const rods = Math.ceil(rebarFt / 20); // standard 20 ft (½") rebar length
    return { area: Math.round(area), blocks, cementBags, sandCuYd, rebarFt, rods };
  }, [length, height, openings, blockType, wallCount]);

  const num = (v, set) => (e) => set(e.target.value);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="build-calculator-page">
      <Link to="/" className="text-sm text-muted-foreground flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Back to shop</Link>
      <div className="flex items-center gap-3 mb-2">
        <div className="h-12 w-12 rounded-xl bg-ink text-white grid place-items-center"><Calculator size={22} /></div>
        <div>
          <h1 className="font-display text-3xl font-bold">Steel &amp; Block Calculator</h1>
          <p className="text-sm text-muted-foreground">Estimate the blocks, cement, sand and steel for a block wall. Estimates only — verify with your contractor.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 bg-card rounded-2xl border border-border p-6 mt-4">
        <label className="text-sm font-semibold flex items-center gap-2"><Ruler size={16} className="text-emerald" /> Wall length (ft)
          <input type="number" min="0" value={length} onChange={num(length, setLength)} className="ml-auto w-28 rounded-lg border border-border px-3 py-2 text-sm" data-testid="calc-length" />
        </label>
        <label className="text-sm font-semibold flex items-center gap-2"><Ruler size={16} className="text-emerald rotate-90" /> Wall height (ft)
          <input type="number" min="0" value={height} onChange={num(height, setHeight)} className="ml-auto w-28 rounded-lg border border-border px-3 py-2 text-sm" data-testid="calc-height" />
        </label>
        <label className="text-sm font-semibold flex items-center gap-2"><Layers size={16} className="text-emerald" /> Number of walls
          <input type="number" min="1" value={wallCount} onChange={num(wallCount, setWallCount)} className="ml-auto w-28 rounded-lg border border-border px-3 py-2 text-sm" data-testid="calc-walls" />
        </label>
        <label className="text-sm font-semibold flex items-center gap-2">Openings (sq ft)
          <input type="number" min="0" value={openings} onChange={num(openings, setOpenings)} className="ml-auto w-28 rounded-lg border border-border px-3 py-2 text-sm" data-testid="calc-openings" />
        </label>
        <label className="text-sm font-semibold flex items-center gap-2 sm:col-span-2">Block size
          <select value={blockType} onChange={num(blockType, setBlockType)} className="ml-auto w-40 rounded-lg border border-border px-3 py-2 text-sm" data-testid="calc-blocktype">
            <option value="6">6 inch block</option>
            <option value="8">8 inch block</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4" data-testid="calc-results">
        {[
          ["Wall area", `${r.area} ft²`],
          ["Concrete blocks", `${r.blocks.toLocaleString()}`],
          ["Cement (mortar)", `${r.cementBags} bags`],
          ["Sand", `${r.sandCuYd} yd³`],
          ["Steel rebar", `${r.rebarFt.toLocaleString()} ft`],
          ["½\" rebar rods (20ft)", `${r.rods}`],
        ].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4" data-testid={`result-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Hammer size={12} /> {k}</div>
            <div className="font-display font-bold text-xl mt-0.5">{v}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">Assumptions: 16"×8" block face (~1.125 blocks/ft²), ~1 cement bag per {blockType === "8" ? "25" : "28"} blocks for mortar, ~3 ft³ sand per bag, vertical steel every 4 ft + horizontal every 2 ft, 20 ft rebar rods. Always confirm with a structural engineer for load-bearing work.</p>
    </div>
  );
}
